<template>
  <div class="color-indication-section">
    <div v-if="loading" class="loading-state">
      {{ 'color_indication.color_indication.loading' | translate }}
    </div>

    <template v-else>
      <!-- First Section: Leftovers, Load Weight, Upload Weight -->
      <div class="section-group">
        <div class="grid-header">
          <div class="grid-header-empty"></div>
          <div class="grid-header-item">
            <span>{{ 'color_indication.color_indication.range_to' | translate }}</span>
            <div class="indicator indicator--green"></div>
          </div>
          <div class="grid-header-item">
            <span>{{ 'color_indication.color_indication.range_to' | translate }}</span>
            <div class="indicator indicator--yellow"></div>
          </div>
          <div class="grid-header-item">
            <span>{{ 'color_indication.color_indication.range_more' | translate }}</span>
            <div class="indicator indicator--red"></div>
          </div>
        </div>

        <!-- Validation Message -->
        <div v-if="firstSectionErrors.length > 0" class="validation-error">
          <div class="error-icon">⚠️</div>
          <div class="error-messages">
            <div v-for="(error, index) in firstSectionErrors" :key="index" class="error-message">
              {{ error }}
            </div>
          </div>
        </div>

        <div class="grid-row" :class="{ 'has-error': hasError('leftovers') }">
          <div class="grid-cell-label">
            <v-switch 
              v-model="settings.leftovers.enabled" 
              color="success"
              :disabled="allValuesZero('leftovers')"
              @input="handleEnabledChange('leftovers', $event)">
            </v-switch>
            <span class="grid-cell-label-item" v-html="$options.filters.translate('color_indication.color_indication.leftovers_label')"></span>
            <div class="info-icon-wrapper">
              <svg class="info-icon" @mouseenter="showTooltip('leftovers')" @mouseleave="hideTooltip">
                <use xlink:href="/svg/sprite.svg#icon-info"/>
              </svg>
              <div v-if="activeTooltip === 'leftovers'" class="tooltip" v-html="$options.filters.translate('color_indication.color_indication.tooltip_leftovers')">
              </div>
            </div>
          </div>
          <div class="grid-cell-input">
            <div class="input-wrapper">
              <input 
                type="number" 
                v-model.number="settings.leftovers.green"
                :min="settings.leftovers.min_value"
                :max="settings.leftovers.max_value"
                step="0.1"
                :class="{ 'input-error': hasError('leftovers') }"
                @mouseenter="showZeroTooltip('leftovers', 'green')"
                @mouseleave="hideZeroTooltip"
                @input="validateField('leftovers')"
                @blur="enforceMinMax('leftovers', 'green')" />
              <div v-if="showZeroTooltipFor === 'leftovers-green' && settings.leftovers.green === 0" class="zero-tooltip" v-html="$options.filters.translate('color_indication.color_indication.zero_tooltip')">
              </div>
            </div>
          </div>
          <div class="grid-cell-input">
            <div class="input-wrapper">
              <input 
                type="number" 
                v-model.number="settings.leftovers.yellow"
                :min="settings.leftovers.min_value"
                :max="settings.leftovers.max_value"
                step="0.1"
                :class="{ 'input-error': hasError('leftovers') }"
                @mouseenter="showZeroTooltip('leftovers', 'yellow')"
                @mouseleave="hideZeroTooltip"
                @input="syncLinkedValues('leftovers', 'yellow')"
                @blur="enforceMinMax('leftovers', 'yellow')" />
              <div v-if="showZeroTooltipFor === 'leftovers-yellow' && settings.leftovers.yellow === 0" class="zero-tooltip" v-html="$options.filters.translate('color_indication.color_indication.zero_tooltip')">
              </div>
            </div>
          </div>
          <div class="grid-cell-input">
            <div class="input-wrapper">
              <input 
                type="number" 
                v-model.number="settings.leftovers.red"
                :min="settings.leftovers.min_value"
                :max="settings.leftovers.max_value"
                step="0.1"
                :class="{ 'input-error': hasError('leftovers') }"
                @mouseenter="showZeroTooltip('leftovers', 'red')"
                @mouseleave="hideZeroTooltip"
                @input="syncLinkedValues('leftovers', 'red')"
                @blur="enforceMinMax('leftovers', 'red')" />
              <div v-if="showZeroTooltipFor === 'leftovers-red' && settings.leftovers.red === 0" class="zero-tooltip" v-html="$options.filters.translate('color_indication.color_indication.zero_tooltip')">
              </div>
            </div>
          </div>
        </div>

        <div class="grid-row" :class="{ 'has-error': hasError('load_weight') }">
          <div class="grid-cell-label">
            <v-switch 
              v-model="settings.load_weight.enabled" 
              color="success"
              :disabled="allValuesZero('load_weight')"
              @input="handleEnabledChange('load_weight', $event)">
            </v-switch>
            <span class="grid-cell-label-item" v-html="$options.filters.translate('color_indication.color_indication.loading_label')"></span>
            <div class="info-icon-wrapper">
              <svg class="info-icon" @mouseenter="showTooltip('load_weight')" @mouseleave="hideTooltip">
                <use xlink:href="/svg/sprite.svg#icon-info"/>
              </svg>
              <div v-if="activeTooltip === 'load_weight'" class="tooltip" v-html="$options.filters.translate('color_indication.color_indication.tooltip_loading')">
              </div>
            </div>
          </div>
          <div class="grid-cell-input">
            <div class="input-wrapper">
              <input 
                type="number" 
                v-model.number="settings.load_weight.green"
                :min="settings.load_weight.min_value"
                :max="settings.load_weight.max_value"
                step="0.1"
                :class="{ 'input-error': hasError('load_weight') }"
                @mouseenter="showZeroTooltip('load_weight', 'green')"
                @mouseleave="hideZeroTooltip"
                @input="validateField('load_weight')"
                @blur="enforceMinMax('load_weight', 'green')" />
              <div v-if="showZeroTooltipFor === 'load_weight-green' && settings.load_weight.green === 0" class="zero-tooltip" v-html="$options.filters.translate('color_indication.color_indication.zero_tooltip')">
              </div>
            </div>
          </div>
          <div class="grid-cell-input">
            <div class="input-wrapper">
              <input 
                type="number" 
                v-model.number="settings.load_weight.yellow"
                :min="settings.load_weight.min_value"
                :max="settings.load_weight.max_value"
                step="0.1"
                :class="{ 'input-error': hasError('load_weight') }"
                @mouseenter="showZeroTooltip('load_weight', 'yellow')"
                @mouseleave="hideZeroTooltip"
                @input="syncLinkedValues('load_weight', 'yellow')"
                @blur="enforceMinMax('load_weight', 'yellow')" />
              <div v-if="showZeroTooltipFor === 'load_weight-yellow' && settings.load_weight.yellow === 0" class="zero-tooltip" v-html="$options.filters.translate('color_indication.color_indication.zero_tooltip')">
              </div>
            </div>
          </div>
          <div class="grid-cell-input">
            <div class="input-wrapper">
              <input 
                type="number" 
                v-model.number="settings.load_weight.red"
                :min="settings.load_weight.min_value"
                :max="settings.load_weight.max_value"
                step="0.1"
                :class="{ 'input-error': hasError('load_weight') }"
                @mouseenter="showZeroTooltip('load_weight', 'red')"
                @mouseleave="hideZeroTooltip"
                @input="syncLinkedValues('load_weight', 'red')"
                @blur="enforceMinMax('load_weight', 'red')" />
              <div v-if="showZeroTooltipFor === 'load_weight-red' && settings.load_weight.red === 0" class="zero-tooltip" v-html="$options.filters.translate('color_indication.color_indication.zero_tooltip')">
              </div>
            </div>
          </div>
        </div>

        <div class="grid-row" :class="{ 'has-error': hasError('upload_weight') }">
          <div class="grid-cell-label">
            <v-switch 
              v-model="settings.upload_weight.enabled" 
              color="success"
              :disabled="allValuesZero('upload_weight')"
              @input="handleEnabledChange('upload_weight', $event)">
            </v-switch>
            <span class="grid-cell-label-item" v-html="$options.filters.translate('color_indication.color_indication.unloading_label')"></span>
            <div class="info-icon-wrapper">
              <svg class="info-icon" @mouseenter="showTooltip('upload_weight')" @mouseleave="hideTooltip">
                <use xlink:href="/svg/sprite.svg#icon-info"/>
              </svg>
              <div v-if="activeTooltip === 'upload_weight'" class="tooltip" v-html="$options.filters.translate('color_indication.color_indication.tooltip_unloading')">
              </div>
            </div>
          </div>
          <div class="grid-cell-input">
            <div class="input-wrapper">
              <input 
                type="number" 
                v-model.number="settings.upload_weight.green"
                :min="settings.upload_weight.min_value"
                :max="settings.upload_weight.max_value"
                step="0.1"
                :class="{ 'input-error': hasError('upload_weight') }"
                @mouseenter="showZeroTooltip('upload_weight', 'green')"
                @mouseleave="hideZeroTooltip"
                @input="validateField('upload_weight')"
                @blur="enforceMinMax('upload_weight', 'green')" />
              <div v-if="showZeroTooltipFor === 'upload_weight-green' && settings.upload_weight.green === 0" class="zero-tooltip" v-html="$options.filters.translate('color_indication.color_indication.zero_tooltip')">
              </div>
            </div>
          </div>
          <div class="grid-cell-input">
            <div class="input-wrapper">
              <input 
                type="number" 
                v-model.number="settings.upload_weight.yellow"
                :min="settings.upload_weight.min_value"
                :max="settings.upload_weight.max_value"
                step="0.1"
                :class="{ 'input-error': hasError('upload_weight') }"
                @mouseenter="showZeroTooltip('upload_weight', 'yellow')"
                @mouseleave="hideZeroTooltip"
                @input="syncLinkedValues('upload_weight', 'yellow')"
                @blur="enforceMinMax('upload_weight', 'yellow')" />
              <div v-if="showZeroTooltipFor === 'upload_weight-yellow' && settings.upload_weight.yellow === 0" class="zero-tooltip" v-html="$options.filters.translate('color_indication.color_indication.zero_tooltip')">
              </div>
            </div>
          </div>
          <div class="grid-cell-input">
            <div class="input-wrapper">
              <input 
                type="number" 
                v-model.number="settings.upload_weight.red"
                :min="settings.upload_weight.min_value"
                :max="settings.upload_weight.max_value"
                step="0.1"
                :class="{ 'input-error': hasError('upload_weight') }"
                @mouseenter="showZeroTooltip('upload_weight', 'red')"
                @mouseleave="hideZeroTooltip"
                @input="syncLinkedValues('upload_weight', 'red')"
                @blur="enforceMinMax('upload_weight', 'red')" />
              <div v-if="showZeroTooltipFor === 'upload_weight-red' && settings.upload_weight.red === 0" class="zero-tooltip" v-html="$options.filters.translate('color_indication.color_indication.zero_tooltip')">
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Second Section: Warehouse Stock -->
      <div class="section-group section-separator">
        <div class="grid-header">
          <div class="grid-header-empty"></div>
          <div class="grid-header-item">
            <span>{{ 'color_indication.color_indication.range_more' | translate }}</span>
            <div class="indicator indicator--green"></div>
          </div>
          <div class="grid-header-item">
            <span>{{ 'color_indication.color_indication.range_to' | translate }}</span>
            <div class="indicator indicator--yellow"></div>
          </div>
          <div class="grid-header-item">
            <span>{{ 'color_indication.color_indication.range_to' | translate }}</span>
            <div class="indicator indicator--red"></div>
          </div>
        </div>

        <!-- Validation Message for Warehouse -->
        <div v-if="warehouseErrors.length > 0" class="validation-error">
          <div class="error-icon">⚠️</div>
          <div class="error-messages">
            <div v-for="(error, index) in warehouseErrors" :key="index" class="error-message">
              {{ error }}
            </div>
          </div>
        </div>

        <div class="grid-row" :class="{ 'has-error': hasError('daily_intake_left') }">
          <div class="grid-cell-label">
            <v-switch 
              v-model="settings.daily_intake_left.enabled" 
              color="success"
              :disabled="allValuesZero('daily_intake_left')"
              @input="handleEnabledChange('daily_intake_left', $event)">
            </v-switch>
            <span class="grid-cell-label-item" v-html="$options.filters.translate('color_indication.color_indication.warehouse_label')"></span>
            <div class="info-icon-wrapper">
              <svg class="info-icon" @mouseenter="showTooltip('daily_intake_left')" @mouseleave="hideTooltip">
                <use xlink:href="/svg/sprite.svg#icon-info"/>
              </svg>
              <div v-if="activeTooltip === 'daily_intake_left'" class="tooltip">
                {{ 'color_indication.color_indication.tooltip_warehouse' | translate }}
              </div>
            </div>
          </div>
          <div class="grid-cell-input">
            <div class="input-wrapper">
              <input 
                type="number" 
                v-model.number="settings.daily_intake_left.green"
                :min="settings.daily_intake_left.min_value"
                :max="settings.daily_intake_left.max_value"
                step="1"
                :class="{ 'input-error': hasError('daily_intake_left') }"
                @mouseenter="showZeroTooltip('daily_intake_left', 'green')"
                @mouseleave="hideZeroTooltip"
                @input="syncLinkedValues('daily_intake_left', 'green')"
                @blur="enforceMinMax('daily_intake_left', 'green')" />
              <div v-if="showZeroTooltipFor === 'daily_intake_left-green' && settings.daily_intake_left.green === 0" class="zero-tooltip" v-html="$options.filters.translate('color_indication.color_indication.zero_tooltip')">
              </div>
            </div>
          </div>
          <div class="grid-cell-input">
            <div class="input-wrapper">
              <input 
                type="number" 
                v-model.number="settings.daily_intake_left.yellow"
                :min="settings.daily_intake_left.min_value"
                :max="settings.daily_intake_left.max_value"
                step="1"
                :class="{ 'input-error': hasError('daily_intake_left') }"
                @mouseenter="showZeroTooltip('daily_intake_left', 'yellow')"
                @mouseleave="hideZeroTooltip"
                @input="syncLinkedValues('daily_intake_left', 'yellow')"
                @blur="enforceMinMax('daily_intake_left', 'yellow')" />
              <div v-if="showZeroTooltipFor === 'daily_intake_left-yellow' && settings.daily_intake_left.yellow === 0" class="zero-tooltip" v-html="$options.filters.translate('color_indication.color_indication.zero_tooltip')">
              </div>
            </div>
          </div>
          <div class="grid-cell-input">
            <div class="input-wrapper">
              <input 
                type="number" 
                v-model.number="settings.daily_intake_left.red"
                :min="settings.daily_intake_left.min_value"
                :max="settings.daily_intake_left.max_value"
                step="1"
                :class="{ 'input-error': hasError('daily_intake_left') }"
                @mouseenter="showZeroTooltip('daily_intake_left', 'red')"
                @mouseleave="hideZeroTooltip"
                @input="validateField('daily_intake_left')"
                @blur="enforceMinMax('daily_intake_left', 'red')" />
              <div v-if="showZeroTooltipFor === 'daily_intake_left-red' && settings.daily_intake_left.red === 0" class="zero-tooltip" v-html="$options.filters.translate('color_indication.color_indication.zero_tooltip')">
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ColorIndicationTab',

  props: {
    loading: {
      type: Boolean,
      default: false
    },
    settings: {
      type: Object,
      required: true
    }
  },

  mounted () {
    this.saveInitialState()

    this.$nextTick(() => {
      setTimeout(() => {
        this.isInitializing = false
      }, 500)
    })
  },

  data () {
    return {
      activeTooltip: null,
      showZeroTooltipFor: null,
      isInitializing: true,
      initialSettings: null,
      errors: {
        leftovers: false,
        load_weight: false,
        upload_weight: false,
        daily_intake_left: false
      }
    }
  },

  computed: {
    firstSectionErrors () {
      const errors = []
      const fields = ['leftovers', 'load_weight', 'upload_weight']

      fields.forEach(key => {
        const setting = this.settings[key]
        if (!setting.enabled) return

        const { green, yellow, red, direction } = setting

        const activeValues = []
        if (green !== 0) activeValues.push(green)
        if (yellow !== 0) activeValues.push(yellow)
        if (red !== 0) activeValues.push(red)

        // Потрібно мінімум 3 значення для валідації (всі кольори)
        if (activeValues.length < 3) return

        // Для less_is_better: зелений < жовтий = червоний
        if (direction === 'less_is_better') {
          if (green >= yellow || green >= red) {
            errors.push(this.$options.filters.translate('color_indication.color_indication.validation_error_less_is_better'))
          }
        }
      })

      return errors
    },

    warehouseErrors () {
      const errors = []
      const setting = this.settings.daily_intake_left

      if (!setting.enabled) return errors

      const { green, yellow, red, direction } = setting

      const activeValues = []
      if (green !== 0) activeValues.push(green)
      if (yellow !== 0) activeValues.push(yellow)
      if (red !== 0) activeValues.push(red)

      // Потрібно мінімум 3 значення для валідації (всі кольори)
      if (activeValues.length < 3) return errors

      // Для more_is_better: зелений = жовтий > червоний
      if (direction === 'more_is_better') {
        if (red >= green || red >= yellow) {
          errors.push(this.$options.filters.translate('color_indication.color_indication.validation_error_more_is_better'))
        }
      }

      return errors
    },

    validationErrors () {
      return [...this.firstSectionErrors, ...this.warehouseErrors]
    }
  },

  watch: {
    'settings.leftovers.green': function () { this.checkAllZeros('leftovers') },
    'settings.leftovers.yellow': function () { this.checkAllZeros('leftovers') },
    'settings.leftovers.red': function () { this.checkAllZeros('leftovers') },
    'settings.load_weight.green': function () { this.checkAllZeros('load_weight') },
    'settings.load_weight.yellow': function () { this.checkAllZeros('load_weight') },
    'settings.load_weight.red': function () { this.checkAllZeros('load_weight') },
    'settings.upload_weight.green': function () { this.checkAllZeros('upload_weight') },
    'settings.upload_weight.yellow': function () { this.checkAllZeros('upload_weight') },
    'settings.upload_weight.red': function () { this.checkAllZeros('upload_weight') },
    'settings.daily_intake_left.green': function () { this.checkAllZeros('daily_intake_left') },
    'settings.daily_intake_left.yellow': function () { this.checkAllZeros('daily_intake_left') },
    'settings.daily_intake_left.red': function () { this.checkAllZeros('daily_intake_left') }
  },

  methods: {
    syncLinkedValues (fieldName, changedColor) {
      if (this.isInitializing) return

      const setting = this.settings[fieldName]
      const value = setting[changedColor]

      // Для залишків, завантаження, розвантаження: синхронізуємо жовтий та червоний
      if (['leftovers', 'load_weight', 'upload_weight'].includes(fieldName)) {
        if (changedColor === 'yellow' || changedColor === 'red') {
          if (value !== 0) {
            // Якщо значення не 0, синхронізуємо обидва поля
            if (changedColor === 'yellow') {
              this.$set(setting, 'red', value)
            } else {
              this.$set(setting, 'yellow', value)
            }
          }
        }
      }

      // Для складу: синхронізуємо зелений та жовтий
      if (fieldName === 'daily_intake_left') {
        if (changedColor === 'green' || changedColor === 'yellow') {
          if (value !== 0) {
            // Якщо значення не 0, синхронізуємо обидва поля
            if (changedColor === 'green') {
              this.$set(setting, 'yellow', value)
            } else {
              this.$set(setting, 'green', value)
            }
          }
        }
      }

      this.$nextTick(() => {
        this.validateField(fieldName)
      })
    },

    showTooltip (type) {
      this.activeTooltip = type
    },

    hideTooltip () {
      this.activeTooltip = null
    },

    showZeroTooltip (fieldName, colorType) {
      this.showZeroTooltipFor = `${fieldName}-${colorType}`
    },

    hideZeroTooltip () {
      this.showZeroTooltipFor = null
    },

    allValuesZero (fieldName) {
      const setting = this.settings[fieldName]
      return setting.green === 0 && setting.yellow === 0 && setting.red === 0
    },

    checkAllZeros (fieldName) {
      if (this.isInitializing) return

      const parts = fieldName.split('.')
      const field = parts[0]

      if (this.allValuesZero(field)) {
        this.$set(this.settings[field], 'enabled', false)

        this.$nextTick(() => {
          this.saveSettings()
        })
      }
    },

    handleEnabledChange (fieldName, enabled) {
      if (this.isInitializing) return

      this.$nextTick(() => {
        this.validateField(fieldName)
        this.saveSettings()
      })
    },

    enforceMinMax (fieldName, colorType) {
      if (this.isInitializing) return

      const setting = this.settings[fieldName]
      let value = setting[colorType]

      if (value < setting.min_value) {
        this.$set(setting, colorType, setting.min_value)
      } else if (value > setting.max_value) {
        this.$set(setting, colorType, setting.max_value)
      }

      this.$nextTick(() => {
        this.validateField(fieldName)
        this.saveSettings()
      })
    },

    validateField (fieldName) {
      const setting = this.settings[fieldName]

      if (!setting.enabled) {
        this.errors[fieldName] = false
        return true
      }
      /* eslint-disable camelcase */
      const { green, yellow, red, min_value, max_value, direction } = setting

      if (green < min_value || green > max_value ||
          yellow < min_value || yellow > max_value ||
          red < min_value || red > max_value) {
        this.errors[fieldName] = true
        return false
      }
      /* eslint-enable camelcase */
      const activeValues = []
      if (green !== 0) activeValues.push({ color: 'green', value: green })
      if (yellow !== 0) activeValues.push({ color: 'yellow', value: yellow })
      if (red !== 0) activeValues.push({ color: 'red', value: red })

      if (activeValues.length < 2) {
        this.errors[fieldName] = false
        return true
      }

      if (['leftovers', 'load_weight', 'upload_weight'].includes(fieldName)) {
        if (activeValues.length === 3) {
          if (direction === 'less_is_better') {
            if (green >= yellow || green >= red) {
              this.errors[fieldName] = true
              return false
            }
          }
        }

        this.errors[fieldName] = false
        return true
      }

      if (fieldName === 'daily_intake_left') {
        if (activeValues.length === 3) {
          if (direction === 'more_is_better') {
            if (red >= green || red >= yellow) {
              this.errors[fieldName] = true
              return false
            }
          }
        }

        this.errors[fieldName] = false
        return true
      }

      this.errors[fieldName] = false
      return true
    },

    hasError (fieldName) {
      return this.errors[fieldName]
    },

    validateAllFields () {
      Object.keys(this.settings).forEach(key => {
        this.validateField(key)
      })
    },

    isValid () {
      return this.validationErrors.length === 0
    },

    getFirstError () {
      if (this.validationErrors.length > 0) {
        return this.validationErrors[0]
      }
      return null
    },

    saveInitialState () {
      this.initialSettings = JSON.parse(JSON.stringify(this.settings))
    },

    hasChanges () {
      if (!this.initialSettings) return true

      const current = JSON.stringify(this.settings)
      const initial = JSON.stringify(this.initialSettings)

      return current !== initial
    },

    saveSettings () {
      if (!this.hasChanges()) {
        return
      }

      if (!this.isValid()) {
        const errorMsg = this.getFirstError()

        this.$toasted.error(errorMsg || this.$options.filters.translate('color_indication.color_indication.validation_fix_errors'), {
          duration: 3000,
          position: 'bottom-right'
        })
        return
      }

      this.$emit('save', this.settings)
      this.saveInitialState()
    }
  }
}
</script>

<style lang="scss" scoped>

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #666;
}

.section-group {
  margin-bottom: 30px;
}

.section-separator {
  padding-top: 30px;
  border-top: 2px solid #E0E0E0;
}

.validation-error {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background-color: #FFF3CD;
  border: 1px solid #FFE69C;
  border-radius: 8px;
  margin-bottom: 20px;
}

.error-icon {
  font-size: 20px;
  line-height: 1;
}

.error-messages {
  flex: 1;
}

.error-message {
  font-size: 13px;
  color: #856404;
  margin-bottom: 4px;
}

.error-message:last-child {
  margin-bottom: 0;
}

.grid-header {
  display: grid;
  grid-template-columns: 1fr 80px 80px 80px;
  gap: 12px;
  margin-bottom: 10px;
  align-items: end;
  padding: 10px 8px;
}

.grid-header-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  font-size: 12px;
  color: #000;
  text-align: center;
}

.indicator {
  width: 22px;
  height: 22px;
  border-radius: 50%;
}

.indicator--green {
  background-color: #91EA91;
}

.indicator--yellow {
  background-color: #FBE268;
}

.indicator--red {
  background-color: #FF9191;
}

.grid-row {
  display: grid;
  grid-template-columns: 1fr 80px 80px 80px;
  gap: 12px;
  align-items: center;
  padding: 10px 8px;
  font-size: 13px;
  transition: background-color 0.2s;
  margin-bottom: 10px;
}

.grid-row.has-error {
  background-color: #FFF3CD;
  border-radius: 8px;
  padding: 10px 8px;
}

.grid-cell-label {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
}

.grid-cell-label-item {
  flex: 1;
}

.info-icon-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.info-icon {
  width: 24px;
  height: 24px;
  color: #4CAF50;
  cursor: help;
  transition: color 0.2s;
}

.info-icon:hover {
  color: #45a049;
}

.tooltip {
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-50%);
  background: white;
  border: 1px solid #D7D7D7;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  width: 280px;
  font-size: 13px;
  line-height: 1.5;
  color: #000;
  pointer-events: none;
}

.tooltip::before {
  content: '';
  position: absolute;
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 6px 6px 6px 0;
  border-color: transparent white transparent transparent;
}

.tooltip::after {
  content: '';
  position: absolute;
  left: -7px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 7px 7px 7px 0;
  border-color: transparent #D7D7D7 transparent transparent;
  z-index: -1;
}

.grid-cell-input {
  display: flex;
  justify-content: center;
}

.input-wrapper {
  position: relative;
  display: inline-block;
}

.grid-cell-input input {
  width: 56px;
  padding: 8px;
  background: #EEEEEE;
  border-radius: 10px;
  font-size: 18px;
  color: #000;
  text-align: center;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.grid-cell-input input:focus {
  outline: none;
  background: #E0E0E0;
}

.grid-cell-input input.input-error {
  border-color: #FF9191;
  background: #FFE5E5;
}

.zero-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  background: white;
  border: 1px solid #D7D7D7;
  border-radius: 8px;
  padding: 10px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  width: 220px;
  font-size: 12px;
  line-height: 1.4;
  color: #000;
  pointer-events: none;
  white-space: normal;
  text-align: center;
}

.grid-cell-input:last-child .zero-tooltip {
  left: auto;
  right: 0;
  transform: none;
}

.grid-cell-input:last-child .zero-tooltip::before {
  left: auto;
  right: 20px;
  transform: none;
}

.grid-cell-input:last-child .zero-tooltip::after {
  left: auto;
  right: 19px;
  transform: none;
}

.zero-tooltip::before {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 6px 6px 0 6px;
  border-color: white transparent transparent transparent;
}

.zero-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 7px 7px 0 7px;
  border-color: #D7D7D7 transparent transparent transparent;
  margin-top: 1px;
  z-index: -1;
}
</style>